import { ArticlePromotionLog } from 'contexts/datamodel/DataModelContext';
import { TreatmentTypeSetting } from 'contexts/structure-edit/useTreatmentTypeSettings';
import { PageDto, PageModule, PageModuleItem, PageType, Structure } from 'data/generated/graphql';
import { convertPageModuleItemsToDataModelEntities } from 'utils/allesseh';
import { resetLayoutIdToBaseline } from 'utils/pageUtils';
import { getNewPageId } from './constants';

interface PreparedPageData {
  root: PageDto['root'];
  metadata: PageDto['metadata'];
  articlePromotionLog: ArticlePromotionLog;
  scratchpad: PageDto['scratchpad'];
  pageType: PageType;
  queryKey: readonly unknown[];
}

/**
 * Builds minimal page data for the layout-selector path without requiring a baseline page DTO.
 * The root shell only needs enough structure for buildRootFromLayoutGroups to work with.
 */
export const buildNewPageDataFromLayout = (currentProperty: string, layoutId: string): PreparedPageData => {
  const newPageId = getNewPageId(currentProperty);
  const root = {
    type: 'Section',
    attributes: {
      pageType: PageType.Section,
      brand: currentProperty,
      routeKey: '/',
      sectionKey: currentProperty,
      sectionLabel: '',
      subtitle: '',
      summaryPriority: 'Default',
      isHidden: false,
      excludeFromLegacy: false,
      entitlements: [],
      adPlacementType: null,
      adZone: null,
      abTestId: null
    },
    collection: [{ type: 'Page', attributes: { layoutId }, collection: [] }]
  } as unknown as PageDto['root'];

  return {
    root,
    metadata: {
      allessehId: newPageId,
      publicationKey: currentProperty,
      name: '[Untitled]',
      createdUtc: 0,
      updatedUtc: 0,
      publishUtc: 0,
      creatorUser: { djUserId: '' },
      revisorUser: { djUserId: '' }
    } as PageDto['metadata'],
    articlePromotionLog: {} as ArticlePromotionLog,
    scratchpad: [],
    pageType: PageType.Section,
    queryKey: ['PageDTOByAllessehId', { allessehId: newPageId, publicationKey: currentProperty }]
  };
};

/**
 * Builds an empty root.collection[0].collection of Module stubs from a layout's structure.
 * Each slot gets uiModuleType + uiModuleFields from the TT catalog, matching what the page editor expects.
 * Also patches root.collection[0].attributes.layoutId to the selected layoutId.
 */
export const buildRootFromStructure = (
  baselineRoot: PageDto['root'],
  structure: Structure,
  layoutId: string,
  ttCatalog: TreatmentTypeSetting[]
): PageDto['root'] => {
  const moduleStubs: Record<string, unknown>[] = structure.groups.flatMap((group) =>
    group.modules.map((slot) => {
      const ttEntry = ttCatalog.find((tt) => tt.treatmentType === slot.ttKey || tt.treatmentTypeKey === slot.ttKey);
      const uiModuleType = ttEntry?.module?.uiModuleType;
      const uiModuleFields = {
        ...(ttEntry?.module?.uiModuleFields ?? {}),
        ...(slot.uiModuleFields ?? {})
      };
      const groupConfig = group.config;

      const rawItems = Array.isArray(slot.moduleItems) ? (slot.moduleItems as PageModuleItem[]) : [];
      const collection = rawItems.length
        ? convertPageModuleItemsToDataModelEntities({ moduleItems: rawItems } as PageModule)
        : [];

      return {
        type: 'Module',
        groupId: group.id,
        slotRef: slot.slotRef,
        isGroupEnabled: groupConfig.defaultValue ?? false,
        attributes: {
          pageModule: {
            ...(uiModuleType ? { uiModuleType } : {}),
            uiModuleFields,
            ...(slot.moduleId != null && { moduleId: slot.moduleId }),
            ...(slot.minItemsOverride != null && { minItemsOverride: slot.minItemsOverride }),
            ...(slot.maxItemsOverride != null && { maxItemsOverride: slot.maxItemsOverride }),
            ...(slot.ignoreBaseQuery != null && { ignoreBaseQuery: slot.ignoreBaseQuery }),
            ...(slot.skipContentParameters != null && { skipContentParameters: slot.skipContentParameters }),
            ...(slot.optional != null && { optional: slot.optional }),
            ...(slot.requiredProperties?.length && { requiredProperties: slot.requiredProperties })
          }
        },
        collection
      };
    })
  );

  const sectionContainer = baselineRoot.collection[0] as Record<string, unknown>;
  return {
    ...baselineRoot,
    collection: [
      {
        ...sectionContainer,
        attributes: {
          ...(sectionContainer.attributes as Record<string, unknown>),
          layoutId
        },
        collection: moduleStubs
      }
    ]
  } as PageDto['root'];
};

interface PreparePageDataParams {
  shouldClone: boolean;
  baselinePageDTO: PageDto;
  clonedResult?: { pageDTOByAllessehId?: unknown };
  pageTitleParam: string | null;
  layoutToFetch: string;
  currentProperty: string | null;
}

/**
 * Prepares page data for rendering based on whether we're cloning a layout or using baseline.
 * When cloning: uses the cloned layout's structure with baseline's metadata (optionally overriding title)
 * When not cloning: uses baseline data directly
 */
export const preparePageData = ({
  shouldClone,
  baselinePageDTO,
  clonedResult,
  pageTitleParam,
  layoutToFetch,
  currentProperty
}: PreparePageDataParams): PreparedPageData => {
  const baselineMetadata = baselinePageDTO.metadata;

  if (shouldClone && clonedResult) {
    const clonedPageDTO = clonedResult.pageDTOByAllessehId as PageDto;
    return {
      root: resetLayoutIdToBaseline(clonedPageDTO.root),
      metadata: pageTitleParam ? { ...baselineMetadata, name: pageTitleParam } : baselineMetadata,
      articlePromotionLog: clonedPageDTO.articlePromotionLog as ArticlePromotionLog,
      scratchpad: clonedPageDTO.scratchpad,
      pageType: clonedPageDTO.root.attributes.pageType as PageType,
      queryKey: ['PageDTOByAllessehId', { allessehId: layoutToFetch, publicationKey: currentProperty ?? '' }]
    };
  }

  const newPageId = getNewPageId(currentProperty ?? '');

  return {
    root: baselinePageDTO.root,
    metadata: baselineMetadata,
    articlePromotionLog: baselinePageDTO.articlePromotionLog as ArticlePromotionLog,
    scratchpad: baselinePageDTO.scratchpad,
    pageType: baselinePageDTO.root.attributes.pageType as PageType,
    queryKey: ['PageDTOByAllessehId', { allessehId: newPageId, publicationKey: currentProperty ?? '' }]
  };
};
